import { useState, useEffect } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { usePlanState } from "../hooks/usePlanState";
import { useNavigate } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUpgradeModal } from "../Components/UpgradeModal";

function FormBuilderSettings() {
    const navigate = useNavigate();
    const { getSettings, updateSettings, setCustomDomain, verifyCustomDomain, removeCustomDomain, error } =
        useFormBuilder();
    const { openUpgradeModal } = useUpgradeModal();

    const [fetching, setFetching] = useState(true);
    const [saving, setSaving] = useState(false);

    // reCAPTCHA
    const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
    const [recaptchaSecretKey, setRecaptchaSecretKey] = useState("");

    // Branding
    const [companyName, setCompanyName] = useState("");
    const [logoUrl, setLogoUrl] = useState("");

    // Custom Domain
    const [domain, setDomain] = useState("");
    const [domainVerified, setDomainVerified] = useState(false);
    const [cnameTarget, setCnameTarget] = useState("");
    const [verifyToken, setVerifyToken] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [domainSaving, setDomainSaving] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [removing, setRemoving] = useState(false);

    const { effectivePlan, planState } = usePlanState();
    const currentPlan = effectivePlan;
    // Custom domain is unlocked by the Agency plan OR the one-time add-on (any paid plan
    // can buy it). Gate purely on the entitlement, not the plan slug. Free users must first
    // move to a paid plan before the add-on is purchasable.
    const canCustomDomain = !!planState?.custom_domain;
    const isFreePlan = effectivePlan === "free" && !planState?.has_ltd;

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSettings = async () => {
        setFetching(true);
        const data = await getSettings();
        if (data) {
            setRecaptchaSiteKey(data.recaptchaSiteKey || "");
            setRecaptchaSecretKey(data.recaptchaSecretKey || "");
            setCompanyName(data.defaultBranding?.companyName || "");
            setLogoUrl(data.defaultBranding?.logoUrl || "");
            setDomain(data.customDomain?.domain || "");
            setDomainVerified(data.customDomain?.verified || false);
            setCnameTarget(data.customDomain?.cnameTarget || "");
            setVerifyToken(data.customDomain?.verifyToken || "");
        }
        setFetching(false);
    };

    const handleSaveRecaptcha = async () => {
        setSaving(true);
        const result = await updateSettings({
            recaptchaSiteKey,
            recaptchaSecretKey,
        });
        setSaving(false);
        if (result) toast("reCAPTCHA keys saved!");
    };

    const handleSaveBranding = async () => {
        setSaving(true);
        const result = await updateSettings({
            defaultBranding: { companyName, logoUrl },
        });
        setSaving(false);
        if (result) toast("Branding saved!");
    };

    const handleSetDomain = async () => {
        if (!domain.trim()) return;
        setDomainSaving(true);
        const result = await setCustomDomain(domain.trim());
        setDomainSaving(false);
        if (result) {
            setCnameTarget(result.cnameTarget || "");
            setVerifyToken(result.verifyToken || "");
            setDomainVerified(false);
            toast("Domain saved! Set up the CNAME record, then verify.");
        }
    };

    const handleVerifyDomain = async () => {
        setVerifying(true);
        const result = await verifyCustomDomain();
        setVerifying(false);
        if (result && result.verified) {
            setDomainVerified(true);
            toast("Domain verified!");
        } else if (result && !result.verified) {
            toast("DNS not found yet. Please wait for propagation and try again.");
        }
    };

    const handleRemoveDomain = async () => {
        setRemoving(true);
        const result = await removeCustomDomain();
        setRemoving(false);
        if (result) {
            setDomain("");
            setCnameTarget("");
            setVerifyToken("");
            setDomainVerified(false);
            setShowRemoveConfirm(false);
            toast("Custom domain removed. Forms now use the default domain.");
        }
    };

    if (fetching) {
        return (
            <div className="min-h-screen lg:w-3/4 md:w-3/4 sm:w-3/4 w-2/3 mx-auto font-Rajdhani">
                <div className="mt-5 space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="border-[1px] dark:border-gray-700 rounded-lg p-4 animate-pulse">
                            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen lg:w-3/4 md:w-3/4 sm:w-3/4 w-2/3 mx-auto font-Rajdhani">
            <div className="mt-5 mb-6">
                <button
                    className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-1"
                    onClick={() => navigate("/form-builder")}
                >
                    &larr; Back to Forms
                </button>
                <h1 className="text-3xl font-bold">Form Builder Settings</h1>
                <p className="text-gray-500 dark:text-gray-400 font-medium mt-1">
                    Configure reCAPTCHA, branding, custom domains, and your plan.
                </p>
            </div>

            <Tabs
                defaultActiveKey="recaptcha"
                id="form-builder-settings-tabs"
                className="mt-3 mb-3 tab font-normal text-black dark:text-gray-200"
                nav-justified
                nav-fill
            >
                {/* reCAPTCHA Tab */}
                <Tab eventKey="recaptcha" title="reCAPTCHA">
                    <div className="border-[1px] dark:border-gray-700 rounded-lg p-5  dark:bg-[#23263a] mt-3">
                        <h2 className="text-xl font-bold mb-1">Google reCAPTCHA v2</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mb-4 text-sm">
                            Protect your forms from spam by adding your own Google reCAPTCHA keys.
                            Get keys from{" "}
                            <a
                                href="https://www.google.com/recaptcha/admin"
                                target="_blank"
                                rel="noreferrer"
                                className="text-indigo-600 dark:text-indigo-400 underline"
                            >
                                Google reCAPTCHA Admin
                            </a>
                            . Select reCAPTCHA v2 &quot;I&apos;m not a robot&quot; checkbox.
                        </p>

                        {currentPlan === "free" && (
                            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-[1px] border-yellow-200 dark:border-yellow-800 rounded-lg text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                Custom reCAPTCHA keys require a Starter plan or above.
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-semibold">Site Key</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded-lg p-2.5 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                                    placeholder="6L..."
                                    value={recaptchaSiteKey}
                                    onChange={(e) => setRecaptchaSiteKey(e.target.value)}
                                    disabled={currentPlan === "free"}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Secret Key</label>
                                <input
                                    type="password"
                                    className="w-full border-[1px] dark:border-gray-600 rounded-lg p-2.5 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                                    placeholder="6L... (stored securely, only last 4 chars shown)"
                                    value={recaptchaSecretKey}
                                    onChange={(e) => setRecaptchaSecretKey(e.target.value)}
                                    disabled={currentPlan === "free"}
                                />
                            </div>
                            <button
                                onClick={handleSaveRecaptcha}
                                disabled={saving || currentPlan === "free"}
                                className="px-5 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold"
                            >
                                {saving ? "Saving..." : "Save Keys"}
                            </button>
                        </div>
                    </div>
                </Tab>

                {/* Branding Tab */}
                <Tab eventKey="branding" title="Branding">
                    <div className="border-[1px] dark:border-gray-700 rounded-lg p-5  dark:bg-[#23263a] mt-3">
                        <h2 className="text-xl font-bold mb-1">Default Branding</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mb-4 text-sm">
                            Set a default company name and logo that appear on your forms.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-semibold">Company Name</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded-lg p-2.5 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                                    placeholder="Your Company Name"
                                    maxLength={200}
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Logo URL</label>
                                <input
                                    type="url"
                                    className="w-full border-[1px] dark:border-gray-600 rounded-lg p-2.5 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                                    placeholder="https://example.com/logo.png"
                                    maxLength={2000}
                                    value={logoUrl}
                                    onChange={(e) => setLogoUrl(e.target.value)}
                                />
                                {logoUrl && (
                                    <div className="mt-2">
                                        <img
                                            src={logoUrl}
                                            alt="Logo preview"
                                            className="h-12 object-contain rounded border p-1"
                                            onError={(e) => { e.target.style.display = "none"; }}
                                        />
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleSaveBranding}
                                disabled={saving}
                                className="px-5 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold"
                            >
                                {saving ? "Saving..." : "Save Branding"}
                            </button>
                        </div>
                    </div>
                </Tab>

                {/* Custom Domain Tab */}
                <Tab eventKey="domain" title="Custom Domain">
                    <div className="border-[1px] dark:border-gray-700 rounded-lg p-5  dark:bg-[#23263a] mt-3">
                        <h2 className="text-xl font-bold mb-1">Custom Domain</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mb-4 text-sm">
                            Serve your forms from your own domain (e.g. forms.yourcompany.com).
                            Included with Agency, or add it to any paid plan as a one-time unlock.
                        </p>

                        {!canCustomDomain && (
                            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border-[1px] border-indigo-200 dark:border-indigo-800 rounded-lg flex flex-wrap items-center justify-between gap-3">
                                <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                                    {isFreePlan
                                        ? "Custom domain is available on paid plans. Upgrade, then add it as a one-time unlock."
                                        : "Custom domain is a one-time add-on. Unlock it to serve forms from your own domain."}
                                </span>
                                {isFreePlan ? (
                                    <button
                                        onClick={() => openUpgradeModal({ trigger: "feature_gate", requestedFeature: "custom_domain", recommendedPlan: "starter" })}
                                        className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold whitespace-nowrap"
                                    >
                                        Upgrade
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => navigate("/settings/billing")}
                                        className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold whitespace-nowrap"
                                    >
                                        Unlock custom domain
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-semibold">Domain</label>
                                <div className="flex gap-2 mt-1">
                                    <input
                                        type="text"
                                        className="flex-1 border-[1px] dark:border-gray-600 rounded-lg p-2.5 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                                        placeholder="forms.yourcompany.com"
                                        value={domain}
                                        onChange={(e) => setDomain(e.target.value)}
                                        disabled={!canCustomDomain}
                                    />
                                    <button
                                        onClick={handleSetDomain}
                                        disabled={domainSaving || !canCustomDomain}
                                        className="px-4 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold whitespace-nowrap"
                                    >
                                        {domainSaving ? "Saving..." : "Set Domain"}
                                    </button>
                                </div>
                            </div>

                            {cnameTarget && (
                                <div className="p-4 bg-gray-50 dark:bg-[#1e2035] border-[1px] dark:border-gray-700 rounded-lg">
                                    <h3 className="text-sm font-bold mb-2">DNS Setup Instructions</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 font-normal mb-3">
                                        Add this CNAME record in your DNS settings to route traffic:
                                    </p>
                                    <div className="dark:bg-[#2c2f44] border-[1px] dark:border-gray-600 rounded p-3 font-mono text-sm">
                                        <div className="flex gap-8">
                                            <div>
                                                <span className="text-gray-400 dark:text-gray-500 text-xs">TYPE</span>
                                                <p className="font-semibold">CNAME</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 dark:text-gray-500 text-xs">NAME</span>
                                                <p className="font-semibold">{domain.split(".")[0]}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 dark:text-gray-500 text-xs">TARGET</span>
                                                <p className="font-semibold text-indigo-600 dark:text-indigo-400">
                                                    {cnameTarget}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {verifyToken && (
                                        <>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 font-normal mt-4 mb-3">
                                                Using a proxied DNS provider (e.g. Cloudflare's orange cloud)? It hides the CNAME,
                                                so also add this TXT record to verify ownership:
                                            </p>
                                            <div className="dark:bg-[#2c2f44] border-[1px] dark:border-gray-600 rounded p-3 font-mono text-sm">
                                                <div className="flex gap-8 flex-wrap">
                                                    <div>
                                                        <span className="text-gray-400 dark:text-gray-500 text-xs">TYPE</span>
                                                        <p className="font-semibold">TXT</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 dark:text-gray-500 text-xs">NAME</span>
                                                        <p className="font-semibold">_cfai-verify.{domain.split(".")[0]}</p>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <span className="text-gray-400 dark:text-gray-500 text-xs">VALUE</span>
                                                        <p className="font-semibold text-indigo-600 dark:text-indigo-400 break-all">
                                                            {verifyToken}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                                        <button
                                            onClick={handleVerifyDomain}
                                            disabled={verifying}
                                            className="px-4 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold"
                                        >
                                            {verifying ? "Verifying..." : "Verify DNS"}
                                        </button>
                                        <span
                                            className={`text-sm font-semibold px-3 py-1 rounded-full ${
                                                domainVerified
                                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                            }`}
                                        >
                                            {domainVerified ? "Verified" : "Pending Verification"}
                                        </span>
                                        <button
                                            onClick={() => setShowRemoveConfirm(true)}
                                            disabled={removing}
                                            className="ml-auto px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm font-semibold"
                                        >
                                            Remove domain
                                        </button>
                                    </div>

                                    <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 font-normal mt-3">
                                        DNS changes can take up to 48 hours to propagate. You can verify at any time.
                                    </p>
                                </div>
                            )}

                            {showRemoveConfirm && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                                    <div className="bg-white dark:bg-[#1e2035] rounded-xl shadow-xl max-w-lg w-full p-6">
                                        <h3 className="text-lg font-bold text-red-600 dark:text-red-400">Remove {domain}?</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 font-normal mt-2">
                                            This disconnects the custom domain from your forms. Before you confirm, know what happens:
                                        </p>
                                        <ul className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-300 font-normal list-disc pl-5">
                                            <li><strong>Existing links break.</strong> Any form link or embed you've already shared using <span className="font-mono">{domain}</span> will stop working immediately.</li>
                                            <li><strong>Live embeds go blank.</strong> Forms embedded on other sites via the custom domain will fail to load until you update them to the default domain.</li>
                                            <li><strong>SSL is released.</strong> The HTTPS certificate for this domain is dropped; re-adding later re-runs DNS verification and certificate issuance.</li>
                                            <li><strong>New copied links</strong> will use the default platform domain instead.</li>
                                            <li>The domain is freed up, so you (or anyone) can add a different one.</li>
                                        </ul>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 font-normal mt-3">
                                            Tip: to switch domains without downtime, add and verify the new domain first, then update your shared links before removing this one.
                                        </p>
                                        <div className="flex justify-end gap-3 mt-5">
                                            <button
                                                onClick={() => setShowRemoveConfirm(false)}
                                                disabled={removing}
                                                className="px-4 py-2 rounded-lg border dark:border-gray-600 text-sm font-semibold disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleRemoveDomain}
                                                disabled={removing}
                                                className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 text-sm font-semibold disabled:opacity-50"
                                            >
                                                {removing ? "Removing..." : "Remove domain"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Tab>

                {/* Plan Tab — plan, usage, add-ons & billing all live in the billing dashboard */}
                <Tab eventKey="plan" title="Plan">
                    <div className="border-[1px] dark:border-gray-700 rounded-lg p-5  dark:bg-[#23263a] mt-3">
                        <h2 className="text-xl font-bold mb-1">Your Plan</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mb-4 text-sm">
                            Current plan:{" "}
                            <span className="capitalize font-bold text-indigo-600 dark:text-indigo-400">
                                {currentPlan}
                            </span>
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-normal mb-4">
                            Manage your plan, usage, add-ons, and billing from the billing dashboard.
                        </p>
                        <button
                            onClick={() => navigate("/settings/billing")}
                            className="py-2 px-4 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Manage plan &amp; billing
                        </button>
                    </div>
                </Tab>
            </Tabs>

            {error && (
                <p className="mt-3 text-red-600 dark:text-red-400 font-medium">
                    {error}
                </p>
            )}

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={localStorage.getItem("theme") === "dark" ? "dark" : "light"}
            />
        </div>
    );
}

export default FormBuilderSettings;
